import { useDraftResumeToast } from "@/hooks/useDraftResumeToast";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getTenantStats } from "@/services/edgeFunctionWrappers/tenantStatsWrapper";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, RefreshCw, Settings2, Mail, Send } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { CreateTenantDialog } from "@/components/super-admin/CreateTenantDialog";
import { ManageAppAssignmentDialog } from "@/components/super-admin/ManageAppAssignmentDialog";
import { ManageEmailConfigDialog } from "@/components/super-admin/ManageEmailConfigDialog";
import { ResendInvitationsDialog } from "@/components/super-admin/ResendInvitationsDialog";
import type { TenantStats } from "@/types/superAdmin";
import { useTranslation } from "react-i18next";

const APP_COLORS: Record<string, string> = {
  StriFit: "bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500/30",
  VB20TN: "bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500/30",
  MPILATES: "bg-pink-500/20 text-pink-700 dark:text-pink-300 border-pink-500/30",
  CROSSFIT_ELITE: "bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-500/30",
  YOGA_STUDIO: "bg-green-500/20 text-green-700 dark:text-green-300 border-green-500/30",
  BOXING_GYM: "bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/30",
};

export default function SuperAdminTenants() {
  const { t } = useTranslation(["superadmin2", "common"]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Saisie tenant interrompue par un reload → proposer de la reprendre.
  useDraftResumeToast([
    {
      formId: "create-tenant",
      message: t("tenants.draftMessage"),
      description: (d) => (d.gymName as string) || undefined,
      isEmpty: (d) => !(d.gymName || d.email || d.adminEmail),
      onResume: () => setCreateDialogOpen(true),
    },
  ]);
  const [appAssignmentDialog, setAppAssignmentDialog] = useState<{
    open: boolean;
    gymId: string;
    gymName: string;
    currentAppName?: string | null;
  }>({
    open: false,
    gymId: "",
    gymName: "",
    currentAppName: null,
  });
  const [emailConfigDialog, setEmailConfigDialog] = useState<{
    open: boolean;
    gymId: string;
    gymName: string;
  }>({
    open: false,
    gymId: "",
    gymName: "",
  });
  const [invitationsDialog, setInvitationsDialog] = useState<{
    open: boolean;
    gymId: string;
    gymName: string;
    appName?: string | null;
  }>({
    open: false,
    gymId: "",
    gymName: "",
    appName: null,
  });
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const { data: tenants = [], isLoading, refetch } = useQuery({
    queryKey: ['tenants'],
    queryFn: getTenantStats,
  });

  const refreshMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('refresh-tenant-stats');
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast({
        title: t("tenants.successTitle"),
        description: t("tenants.refreshSuccessDesc"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("tenants.errorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <SuperAdminLayout>
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground">{t("tenants.title")}</h1>
            <p className="text-sm md:text-base text-muted-foreground">{t("tenants.subtitle")}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
              variant="outline"
              className="flex-1 md:flex-none"
              size={isMobile ? "sm" : "default"}
            >
              <RefreshCw className={`h-4 w-4 ${isMobile ? '' : 'mr-2'} ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
              {!isMobile && t("tenants.refresh")}
            </Button>
            <Button
              onClick={() => setCreateDialogOpen(true)}
              className="flex-1 md:flex-none"
              size={isMobile ? "sm" : "default"}
            >
              <Plus className="h-4 w-4 mr-2" />
              {isMobile ? t("tenants.newShort") : t("tenants.newTenant")}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-muted-foreground">{t("tenants.loading")}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {tenants.map((tenant) => (
              <Card key={tenant.gym_id} className="hover:bg-accent transition-colors">
                <CardHeader className="p-3 md:p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-foreground text-base md:text-lg">{tenant.gym_name}</CardTitle>
                      <p className="text-xs md:text-sm text-muted-foreground">{tenant.city}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setInvitationsDialog({
                            open: true,
                            gymId: tenant.gym_id,
                            gymName: tenant.gym_name,
                            appName: tenant.app_name,
                          })
                        }
                        title={t("tenants.resendInvitations")}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setEmailConfigDialog({
                            open: true,
                            gymId: tenant.gym_id,
                            gymName: tenant.gym_name,
                          })
                        }
                        title={t("tenants.emailConfig")}
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setAppAssignmentDialog({
                            open: true,
                            gymId: tenant.gym_id,
                            gymName: tenant.gym_name,
                            currentAppName: tenant.app_name,
                          })
                        }
                        title={t("tenants.appConfig")}
                      >
                        <Settings2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {tenant.app_name && (
                    <Badge
                      variant="outline"
                      className={`mt-2 w-fit ${APP_COLORS[tenant.app_name] || "bg-muted text-muted-foreground"}`}
                    >
                      {tenant.app_name}
                    </Badge>
                  )}
                </CardHeader>
                <CardContent className="p-3 md:p-6 pt-0">
                  <div className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
                    <div className="flex justify-between text-muted-foreground">
                      <span>{t("tenants.totalUsers")}</span>
                      <span className="font-semibold">{tenant.total_users}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>{t("tenants.admins")}</span>
                      <span className="font-semibold">{tenant.admin_count}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>{t("tenants.coaches")}</span>
                      <span className="font-semibold">{tenant.coach_count}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>{t("tenants.clients")}</span>
                      <span className="font-semibold">{tenant.client_count}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>{t("tenants.activeSubs")}</span>
                      <span className="font-semibold">{tenant.active_subscriptions}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>{t("tenants.revenue30d")}</span>
                      <span className="font-semibold">{Number(tenant.revenue_last_30d).toFixed(2)} DT</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>{t("tenants.bookings30d")}</span>
                      <span className="font-semibold">{tenant.total_bookings_last_30d}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <CreateTenantDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={() => {
          refetch();
          setCreateDialogOpen(false);
        }}
      />

      <ManageAppAssignmentDialog
        open={appAssignmentDialog.open}
        onOpenChange={(open) =>
          setAppAssignmentDialog((prev) => ({ ...prev, open }))
        }
        gymId={appAssignmentDialog.gymId}
        gymName={appAssignmentDialog.gymName}
        currentAppName={appAssignmentDialog.currentAppName}
        onSuccess={() => {
          refetch();
        }}
      />

      <ManageEmailConfigDialog
        open={emailConfigDialog.open}
        onOpenChange={(open) =>
          setEmailConfigDialog((prev) => ({ ...prev, open }))
        }
        gymId={emailConfigDialog.gymId}
        gymName={emailConfigDialog.gymName}
      />

      <ResendInvitationsDialog
        open={invitationsDialog.open}
        onOpenChange={(open) =>
          setInvitationsDialog((prev) => ({ ...prev, open }))
        }
        gymId={invitationsDialog.gymId}
        gymName={invitationsDialog.gymName}
        appName={invitationsDialog.appName}
      />
    </SuperAdminLayout>
  );
}
